import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronSquareRight, faChevronSquareDown } from '@fortawesome/pro-solid-svg-icons';
import { Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material';
import {
	CerebroIcon,
	CerebroStatusIndicatorTableCell,
	CerebroText,
	StatusIndicatorColor,
} from '../../shared/components';
import { MilestoneReport, UnlockUser, DataRoomUser, DataRoom, AssociatedTeamMember } from '../../shared/models';
import { TableCellMilestoneIcon } from './TableCellMilestoneIcon';
import React from 'react';
import { DateUtils } from '../../shared/utils';
import { TeamMemberActionButton } from '../Participants/Lenders/TeamMemberActionButton';
import { useAuth } from '../../shared/context';
import { GetDataRoomUserFromMilestoneReport } from '../../shared/utils/entity';
import { LenderParticipantActionButton } from '../Participants/Lenders/LenderParticipantActionButton';

interface MilestoneRowProps {
	milestone: MilestoneReport;
	teamMembers: AssociatedTeamMember[];
	dataRoom: DataRoom;
	onDetails: (milestone: MilestoneReport | null) => void;
	unlockUser: (user: UnlockUser) => void;
	accessLogs: (user: UnlockUser) => void;
	optOutUser: (user: DataRoomUser) => void;
	addNote: (user: DataRoomUser) => void;
	viewNotes: (user: DataRoomUser) => void;
	addTeamMember: (user: DataRoomUser) => void;
	removeTeamMember: (member: AssociatedTeamMember) => void;
	sendInvite: (user: UnlockUser) => void;
	sendEmail: (user: DataRoomUser) => void;
	viewEmails: (user: DataRoomUser) => void;
	uploadNda: (user: DataRoomUser) => void;
	grantAccess: (user: DataRoomUser) => void;
	deleteDataRoomUser: (user: DataRoomUser) => void;
	viewOptOut: (user: DataRoomUser) => void;
	contactDetails: (user: DataRoomUser) => void;
	teamMemberDetails: (user: AssociatedTeamMember) => void;
}

export const MilestoneRow: React.FC<MilestoneRowProps> = ({
	milestone,
	teamMembers,
	dataRoom,
	onDetails,
	unlockUser,
	accessLogs,
	optOutUser,
	addNote,
	viewNotes,
	addTeamMember,
	removeTeamMember,
	sendInvite,
	sendEmail,
	viewEmails,
	uploadNda,
	grantAccess,
	deleteDataRoomUser,
	viewOptOut,
	contactDetails,
	teamMemberDetails,
}) => {
	const { currentUser } = useAuth();
	const user = GetDataRoomUserFromMilestoneReport(milestone) ?? ({} as DataRoomUser);
	const callInProgress =
		milestone.dataRoomCallGuid != null &&
		milestone.callStatus != 'Completed' &&
		!milestone.hasCompletedCalls &&
		milestone.callStatus != 'Cancelled';
	const [toggleDetailRow, setToggleDetailRow] = React.useState<boolean>(false);

	const getStatusColor = (milestone: MilestoneReport): StatusIndicatorColor => {
		if (milestone.activeUser) {
			return 'Green';
		}

		return milestone.optedOut ||
			milestone.roomDeclineReasonList?.length > 0 ||
			milestone.otherDeclineReason ||
			milestone.otherRoomDeclineReason
			? 'Red'
			: 'Yellow';
	};

	const getStatusTooltip = (milestone: MilestoneReport): string => {
		if (milestone.activeUser) {
			return 'Active';
		}

		return milestone.optedOut ||
			milestone.roomDeclineReasonList?.length > 0 ||
			milestone.otherDeclineReason ||
			milestone.otherRoomDeclineReason
			? 'Opted-Out'
			: 'Inactive';
	};

	return (
		<>
			<TableRow>
				<CerebroStatusIndicatorTableCell
					colorType={getStatusColor(milestone)}
					tooltip={getStatusTooltip(milestone)}
				/>
				<TableCell>
					<CerebroIcon
						sx={{ cursor: 'pointer' }}
						title={toggleDetailRow ? 'Hide team member detail row' : 'Show team member detail row'}
						onClick={() => setToggleDetailRow(!toggleDetailRow)}
					>
						{toggleDetailRow ? (
							<FontAwesomeIcon icon={faChevronSquareDown} size="lg" />
						) : (
							<FontAwesomeIcon icon={faChevronSquareRight} size="lg" />
						)}
					</CerebroIcon>
				</TableCell>
				<TableCell>
					<CerebroText>{milestone.bankName}</CerebroText>
				</TableCell>
				<TableCellMilestoneIcon milestoneType={'Teaser'} response={milestone.teaser} inProgress={false} />
				<TableCellMilestoneIcon
					milestoneType={'AccessDeal'}
					response={milestone.accessDeal}
					inProgress={false}
				/>
				<TableCellMilestoneIcon
					milestoneType={'DocumentsDownloaded'}
					response={milestone.documentsDownloaded}
					inProgress={false}
				/>
				<TableCellMilestoneIcon
					milestoneType={'ReadyForCall'}
					response={milestone.readyForCall}
					inProgress={callInProgress}
				/>
				<TableCellMilestoneIcon
					milestoneType={'TermSheetSubmitted'}
					response={milestone.termSheetSubmitted}
					inProgress={false}
				/>
				<TableCellMilestoneIcon
					milestoneType={'Underwriting'}
					response={milestone.underwriting}
					inProgress={false}
				/>
				<TableCellMilestoneIcon milestoneType={'Funded'} response={milestone.funded} inProgress={false} />
				<TableCell>
					<LenderParticipantActionButton
						isEmployee={currentUser.isEmployeeUser}
						isBorrower={currentUser.isCompanyUser}
						isBanker={currentUser.isBankUser}
						dataRoom={dataRoom}
						user={user}
						milestone={milestone}
						unlockUser={unlockUser}
						accessLogs={accessLogs}
						optOutUser={optOutUser}
						addNote={addNote}
						viewNotes={viewNotes}
						addTeamMember={addTeamMember}
						sendInvite={sendInvite}
						viewEmails={viewEmails}
						sendEmail={sendEmail}
						uploadNda={uploadNda}
						grantAccess={grantAccess}
						deleteDataRoomUser={deleteDataRoomUser}
						contactDetails={contactDetails}
						progressDetails={onDetails}
					/>
				</TableCell>
			</TableRow>
			{toggleDetailRow && (
				<TableRow>
					<TableCell colSpan={2}></TableCell>
					<TableCell colSpan={10}>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell>Name</TableCell>
									<TableCell>Email</TableCell>
									<TableCell>Phone</TableCell>
									<TableCell>Last Accessed</TableCell>
									<TableCell></TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								<TableRow>
									<TableCell>
										{milestone.firstName} {milestone.lastName}
									</TableCell>
									<TableCell>{milestone.emailAddress}</TableCell>
									<TableCell>{milestone.phone}</TableCell>
									<TableCell>
										{DateUtils.FormatStringAsShortDate(milestone.lastAccessedOn?.toString())}
									</TableCell>
									<TableCell></TableCell>
								</TableRow>
								{teamMembers.map(item => {
									return (
										<TableRow key={item.guid}>
											<TableCell>
												{item.firstName} {item.lastName}
											</TableCell>
											<TableCell>{item.emailAddress}</TableCell>
											<TableCell>{item.phone}</TableCell>
											<TableCell>
												{DateUtils.FormatStringAsShortDate(item.lastAccessedOn?.toString())}
											</TableCell>
											<TableCell>
												<TeamMemberActionButton
													isEmployee={currentUser.isEmployeeUser}
													isBorrower={currentUser.isCompanyUser}
													isBanker={currentUser.isBankUser}
													user={user}
													dataRoom={dataRoom}
													member={item}
													unlockUser={unlockUser}
													accessLogs={accessLogs}
													removeTeamMember={removeTeamMember}
													sendInvite={sendInvite}
													contactDetails={teamMemberDetails}
												/>
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</TableCell>
				</TableRow>
			)}
		</>
	);
};
